/* ============================================================
   Админ-панель нового дизайна: сайдбар (9 разделов) + топбар
   + контент. Заявки на участие — реальный бэкенд, контент
   витрины — локальный контент-стор.
   ============================================================ */
import { useEffect, useMemo, useState } from 'react';
import { API_URL, apiFetch, useAppStore } from '../../store';
import { useFedStore } from '../store';
import { useFedContent } from '../content';
import { Ic } from '../icons';
import AdmDashboard from './Dashboard';
import { AdmTournamentsList, AdmTournamentEditor } from './Tournaments';
import { AdmNewsList, AdmNewsEditor } from './News';
import AdmRegs, { regStatus } from './Regs';
import AdmSponsors from './Sponsors';
import AdmList from './Lists';
import AdmSettings from './Settings';
import AdmUsers from './Users';
import AdmNotify from './Notify';
import AdmInbox from './Inbox';
import AdmDocs from './Docs';
import AdmLive from './Live';
import AdmMatchCenter from './MatchCenter';
import AdmGallery from './Gallery';

const NAV = [
  { key: 'dash', label: 'Дашборд', icon: 'grid' },
  { key: 'tours', label: 'Турниры', icon: 'trophy' },
  { key: 'news', label: 'Новости', icon: 'news' },
  { key: 'regs', label: 'Заявки', icon: 'inbox', badge: 'regs' },
  { key: 'inbox', label: 'Обращения', icon: 'phone', badge: 'inbox' },
  { key: 'sponsors', label: 'Спонсоры', icon: 'money', badge: 'sponsors' },
  { key: 'live', label: 'Эфир', icon: 'video' },
  { key: 'matches', label: 'Матч-центр', icon: 'layers' },
  { key: 'docs', label: 'Документы', icon: 'doc' },
  { key: 'gallery', label: 'Галерея', icon: 'image' },
  { key: 'partners', label: 'Партнёры', icon: 'hand' },
  { key: 'projects', label: 'Проекты', icon: 'layers' },
  { key: 'winners', label: 'Победители', icon: 'medal' },
  { key: 'team', label: 'Команда', icon: 'community' },
  { key: 'users', label: 'Пользователи', icon: 'user' },
  { key: 'notify', label: 'Уведомления', icon: 'bell' },
  { key: 'settings', label: 'Настройки', icon: 'gear' },
];

const TITLES = {
  dash: 'Дашборд', tours: 'Турниры', tourEdit: 'Турнир', news: 'Новости', newsEdit: 'Пост',
  regs: 'Заявки на участие', inbox: 'Обращения', sponsors: 'Спонсорские заявки',
  live: 'Эфир', matches: 'Матч-центр', docs: 'Документы', gallery: 'Галерея', partners: 'Партнёры',
  projects: 'Проекты', winners: 'Победители', team: 'Команда федерации', users: 'Пользователи',
  notify: 'Уведомления', settings: 'Настройки',
  more: 'Меню',
};

// краткий подзаголовок под крупным заголовком раздела (мобильный вид)
const SUBTITLES = {
  dash: 'Обзор турниров и заявок', tours: 'Турниры и лиги федерации', news: 'Публикации на витрине',
  regs: 'Заявки участников на модерации', inbox: 'Письма с формы «Контакты»',
  sponsors: 'Заявки на спонсорство', live: 'Трансляция и расписание эфира', matches: 'Live-счёт и статистика матчей',
  docs: 'Регламенты и положения', gallery: 'Фотографии с мероприятий', partners: 'Партнёры и спонсоры',
  projects: 'Проекты и направления', winners: 'Зал славы федерации', team: 'Состав федерации',
  users: 'Все аккаунты и восстановление паролей',
  notify: 'Рассылка сообщений и пушей пользователям', settings: 'Общие настройки',
  more: 'Все разделы админки',
};

// нижний док на мобиле: 4 основных раздела + «Ещё» (полный список в drawer)
const DOCK = [
  { key: 'dash', label: 'Дашборд', icon: 'grid' },
  { key: 'regs', label: 'Заявки', icon: 'inbox', badge: 'regs' },
  { key: 'tours', label: 'Турниры', icon: 'trophy' },
  { key: 'news', label: 'Новости', icon: 'news' },
  { key: 'more', label: 'Ещё', icon: 'more' },
];
const PRIMARY_KEYS = ['dash', 'regs', 'tours', 'news'];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('ru-RU'); } catch (e) { return String(iso).slice(0, 10); }
};

export default function FedAdminApp() {
  const user = useAppStore((s) => s.user);
  const logout = useAppStore((s) => s.logout);
  const fetchApplications = useAppStore((s) => s.fetchApplications);
  const fetchSupportTickets = useAppStore((s) => s.fetchSupportTickets);
  const { go } = useFedStore();
  const sponsorRequests = useFedContent((s) => s.sponsorRequests);
  const tournaments = useFedContent((s) => s.tournaments);
  const fetchSponsorRequests = useFedContent((s) => s.fetchSponsorRequests);

  const [section, setSection] = useState('dash');
  const [editId, setEditId] = useState(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [rawApps, setRawApps] = useState([]);
  const [teamStates, setTeamStates] = useState({});
  const [newTickets, setNewTickets] = useState(0);

  const isAdmin = user && (user.role === 'admin' || user.user_type === 'admin');

  const loadApps = async () => {
    try {
      const apps = await fetchApplications();
      if (Array.isArray(apps)) setRawApps(apps);
      else if (apps && Array.isArray(apps.applications)) setRawApps(apps.applications);
    } catch (e) { /* бэкенд недоступен — таблица будет пустой */ }
  };
  const loadTeamApps = async () => {
    const managed = tournaments.filter((t) => t.hasData === 'mlbb' || t.hasData === 'team');
    const entries = await Promise.all(managed.map(async (t) => {
      const discipline = `${t.disc || ''} ${t.game || ''}`.toLowerCase();
      const game = t.teamGame || (t.hasData === 'mlbb' ? 'mlbb' : discipline.includes('counter') || discipline.includes('cs2') ? 'cs2' : String(t.disc || '').toLowerCase());
      try {
        const res = await apiFetch(`${API_URL}/fed-teams/state?tour=${encodeURIComponent(t.id)}&game=${encodeURIComponent(game)}`);
        if (!res.ok) return [t.id, null];
        return [t.id, { ...await res.json(), game }];
      } catch (e) {
        return [t.id, null];
      }
    }));
    setTeamStates(Object.fromEntries(entries.filter(([, state]) => state)));
  };
  useEffect(() => {
    if (!isAdmin) return;
    loadApps();
    loadTeamApps();
    fetchSponsorRequests();
    fetchSupportTickets('new').then((d) => setNewTickets(d.new_count || 0)).catch(() => {});
  }, [isAdmin, tournaments]); // eslint-disable-line react-hooks/exhaustive-deps

  const regs = useMemo(
    () =>
      (rawApps || []).map((a) => ({
        id: a.id,
        nick: a.user?.nickname || a.game_nick || a.fio || '—',
        name: a.user?.full_name || a.fio || '',
        tournament: a.tournament_title || a.tournament?.title || 'CS2 OPEN · СКО',
        tournamentKey: a.tournament_key || null,
        contact: a.user?.email || a.phone || '—',
        date: fmtDate(a.created_at),
        status: regStatus(a.status),
        raw: a,
      })),
    [rawApps]
  );

  const teamRegs = useMemo(() => tournaments.flatMap((t) => {
    const state = teamStates[t.id];
    if (!state) return [];
    const teams = new Map((state.teams || []).map((team) => [team.id, team]));
    return (state.regs || []).map((registration) => {
      const team = teams.get(registration.teamId);
      if (!team) return null;
      const captain = (team.members || []).find((member) => member.key === team.captain);
      return {
        id: `team:${t.id}:${team.id}`,
        kind: 'team',
        feeConfirmed: registration.feeConfirmed === true,
        hasFeeStatus: (t.teamGame || state.game) === 'cs2',
        nick: team.name,
        name: `${team.tag} · ${(team.members || []).length}/5`,
        tournament: t.title,
        tournamentKey: t.id,
        contact: captain?.tg || '—',
        date: fmtDate(registration.createdAt),
        status: 'approved',
        rawTeam: team,
      };
    }).filter(Boolean);
  }), [teamStates, tournaments]);

  const allRegs = useMemo(() => [...teamRegs, ...regs], [teamRegs, regs]);

  const badges = {
    regs: allRegs.filter((r) => r.status === 'new').length,
    sponsors: sponsorRequests.filter((r) => r.status === 'new').length,
    inbox: newTickets,
  };

  // защита: только для админов
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 text-center" style={{ minHeight: '60vh', padding: 24 }}>
        <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 26 }}>Доступ только для администраторов</div>
        <p className="text-fg-muted" style={{ fontSize: 14 }}>Войдите в аккаунт администратора, чтобы открыть панель.</p>
        <button onClick={() => go('auth', { authMode: 'login' })} className="fed-btn-primary h-11 px-6" style={{ fontSize: 14 }}>Войти</button>
      </div>
    );
  }

  const goSection = (key, extra = {}) => {
    setSection(key);
    setEditId(extra.editId ?? null);
    setSideOpen(false);
    window.scrollTo(0, 0);
  };

  const activeGroup = section === 'tourEdit' ? 'tours' : section === 'newsEdit' ? 'news' : section;
  const isEditor = section === 'tourEdit' || section === 'newsEdit';

  const primary = {
    tours: { label: 'Создать турнир', onClick: () => goSection('tourEdit', { editId: null }) },
    news: { label: 'Новый пост', onClick: () => goSection('newsEdit', { editId: null }) },
    partners: null, projects: null, winners: null,
  }[activeGroup];

  return (
    <div className="fed-admin-shell">
      {/* скрим для мобильного сайдбара */}
      {sideOpen && <div className="fed-side-scrim" onClick={() => setSideOpen(false)} />}

      {/* ===== сайдбар ===== */}
      <aside
        className={`fed-admin-sidebar flex flex-col border-r border-line ${sideOpen ? 'open' : ''}`}
        style={{ background: 'linear-gradient(180deg,var(--fed-surface),var(--fed-bg))' }}
      >
        <div className="flex items-center gap-3 border-b border-line" style={{ padding: '18px 16px' }}>
          <img
            src="/assets/logo-federation.png"
            alt=""
            className="rounded-full object-cover"
            style={{ width: 40, height: 40, border: '1.5px solid rgba(225,18,23,.55)', boxShadow: '0 0 14px -4px var(--fed-accent-glow)' }}
          />
          <div className="flex flex-col leading-none">
            <span className="font-display font-bold text-fg" style={{ fontSize: 15 }}>Админ-панель</span>
            <span className="font-mono uppercase text-fg-muted mt-1" style={{ fontSize: 9, letterSpacing: '.18em' }}>Федерация СКО</span>
          </div>
        </div>

        <nav className="flex-1 flex flex-col gap-[3px]" style={{ padding: 12 }}>
          {NAV.map((item) => {
            const active = activeGroup === item.key;
            const badge = item.badge ? badges[item.badge] : 0;
            return (
              <button
                key={item.key}
                onClick={() => goSection(item.key)}
                className="flex items-center gap-3 rounded-[10px] cursor-pointer font-display font-semibold uppercase border-none text-left"
                style={{
                  padding: '11px 13px', fontSize: 14.5, letterSpacing: '.02em',
                  background: active ? 'rgba(225,18,23,.12)' : 'transparent',
                  color: active ? 'var(--fed-fg)' : 'var(--fed-fg-soft)',
                }}
              >
                <span style={{ width: 19, display: 'inline-flex', color: active ? 'var(--fed-accent-bright)' : 'var(--fed-fg-muted)' }}>
                  <Ic name={item.icon} size={18} />
                </span>
                <span className="flex-1">{item.label}</span>
                {badge > 0 && (
                  <span className="rounded-full text-white font-mono" style={{ fontSize: 10, padding: '2px 7px', background: 'var(--fed-accent)' }}>{badge}</span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2.5 border-t border-line" style={{ padding: 14 }}>
          <span
            className="flex items-center justify-center rounded-full font-display font-bold text-white flex-shrink-0"
            style={{ width: 36, height: 36, fontSize: 15, background: 'linear-gradient(140deg,var(--fed-accent),var(--fed-accent-deep))' }}
          >
            {(user?.nickname || 'А')[0].toUpperCase()}
          </span>
          <div className="min-w-0 flex-1 leading-none">
            <div className="font-semibold text-fg truncate" style={{ fontSize: 13 }}>{user?.nickname || 'Администратор'}</div>
            <div className="font-mono text-fg-faint truncate" style={{ fontSize: 10, marginTop: 3 }}>{user?.email || ''}</div>
          </div>
          <button className="fed-iconbtn" title="Выйти" onClick={logout}>
            <Ic name="logout" size={16} />
          </button>
        </div>
      </aside>

      {/* ===== main ===== */}
      <main className="flex-1 min-w-0">
        {/* топбар */}
        <header
          className="fed-admin-topbar sticky top-0 z-[100] flex items-center gap-3 border-b border-line"
          style={{ height: 66, padding: '0 20px', background: 'rgba(10,10,12,.86)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}
        >
          {/* назад из редакторов; иначе на мобиле — открыть меню */}
          {isEditor ? (
            <button className="fed-iconbtn" style={{ width: 40, height: 40 }} onClick={() => goSection(section === 'tourEdit' ? 'tours' : 'news')}>
              <Ic name="chevR" size={18} sw={2.4} style={{ transform: 'rotate(180deg)' }} />
            </button>
          ) : (
            <button className="fed-side-toggle fed-iconbtn" style={{ width: 40, height: 40 }} onClick={() => goSection('more')}>
              <Ic name="burger" size={18} sw={2} />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <div className="font-mono uppercase text-fg-faint max-[900px]:hidden" style={{ fontSize: 10, letterSpacing: '.14em' }}>Федерация киберспорта СКО</div>
            <h1 className="truncate" style={{ fontSize: 22, marginTop: 3 }}>{TITLES[section] || 'Админ-панель'}</h1>
          </div>
          <button onClick={() => go('home')} className="fed-btn-ghost h-10 px-3.5 rounded-[10px] gap-2" style={{ fontSize: 12.5 }} title="На сайт">
            <Ic name="external" size={15} />
            <span className="max-[520px]:hidden">Сайт</span>
          </button>
          {primary && (
            <button
              onClick={primary.onClick}
              className="h-10 px-4 border-none rounded-[10px] cursor-pointer font-display font-bold uppercase text-white inline-flex items-center gap-2"
              style={{ fontSize: 13, letterSpacing: '.04em', background: 'linear-gradient(120deg,var(--fed-accent-bright),var(--fed-accent-deep))', boxShadow: '0 10px 26px -12px var(--fed-accent-glow)' }}
            >
              <Ic name="plus" size={15} sw={2.4} />
              <span className="max-[520px]:hidden">{primary.label}</span>
            </button>
          )}
        </header>

        {/* контент */}
        <div className="fed-admin-mainpad" style={{ padding: 24, maxWidth: 1400 }}>
          {/* крупный заголовок раздела — только на мобиле (как в старой админке) */}
          {!isEditor && (
            <div className="fed-adm-section-head min-[901px]:hidden" style={{ marginBottom: 18 }}>
              <h2 style={{ fontSize: 28 }}>{TITLES[section]}</h2>
              <p className="text-fg-muted" style={{ fontSize: 13.5, marginTop: 5 }}>{SUBTITLES[section]}</p>
            </div>
          )}

          {/* ===== страница «Ещё»: полный список разделов (мобильная) ===== */}
          {section === 'more' && (
            <div className="fed-adm-more" style={{ maxWidth: 620 }}>
              <div className="fed-adm-services">
                {NAV.map((item) => {
                  const badge = item.badge ? badges[item.badge] : 0;
                  return (
                    <button key={item.key} onClick={() => goSection(item.key)} className="fed-adm-service">
                      <span className="fed-adm-service-icon">
                        <Ic name={item.icon} size={19} />
                        {badge > 0 && <i>{badge > 99 ? '99+' : badge}</i>}
                      </span>
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* профиль + действия */}
              <div className="fed-admin-card fed-adm-profile flex items-center gap-3 rounded-2xl border border-line bg-surface">
                <span
                  className="flex items-center justify-center rounded-full font-display font-bold text-white flex-shrink-0"
                  style={{ width: 42, height: 42, fontSize: 16, background: 'linear-gradient(140deg,var(--fed-accent),var(--fed-accent-deep))' }}
                >
                  {(user?.nickname || 'А')[0].toUpperCase()}
                </span>
                <div className="min-w-0 flex-1 leading-none">
                  <div className="font-semibold text-fg truncate" style={{ fontSize: 14 }}>{user?.nickname || 'Администратор'}</div>
                  <div className="font-mono text-fg-faint truncate" style={{ fontSize: 11, marginTop: 4 }}>{user?.email || ''}</div>
                </div>
              </div>
              <div className="fed-adm-more-actions grid grid-cols-2 gap-2.5">
                <button onClick={() => go('home')} className="fed-btn-ghost h-12 rounded-xl gap-2" style={{ fontSize: 13.5 }}>
                  <Ic name="external" size={16} /> На сайт
                </button>
                <button
                  onClick={logout}
                  className="h-12 rounded-xl cursor-pointer border font-display font-semibold uppercase inline-flex items-center justify-center gap-2 text-fg-muted"
                  style={{ fontSize: 13.5, borderColor: 'var(--fed-line-accent)', background: 'rgba(225,18,23,.06)' }}
                >
                  <Ic name="logout" size={16} /> Выйти
                </button>
              </div>
            </div>
          )}

          {section === 'dash' && <AdmDashboard regs={allRegs} goSection={goSection} />}
          {section === 'tours' && <AdmTournamentsList onEdit={(id) => goSection('tourEdit', { editId: id })} regs={allRegs} />}
          {section === 'tourEdit' && <AdmTournamentEditor key={editId || 'new'} tourId={editId} onBack={() => goSection('tours')} onTeamsChanged={loadTeamApps} />}
          {section === 'news' && <AdmNewsList onEdit={(id) => goSection('newsEdit', { editId: id })} />}
          {section === 'newsEdit' && <AdmNewsEditor key={editId || 'new'} newsId={editId} onBack={() => goSection('news')} />}
          {section === 'regs' && <AdmRegs regs={allRegs} reload={() => Promise.all([loadApps(), loadTeamApps()])} />}
          {section === 'inbox' && <AdmInbox />}
          {section === 'sponsors' && <AdmSponsors />}
          {section === 'live' && <AdmLive />}
          {section === 'matches' && <AdmMatchCenter />}
          {section === 'docs' && <AdmDocs />}
          {section === 'gallery' && <AdmGallery />}
          {section === 'partners' && <AdmList listKey="partners" />}
          {section === 'projects' && <AdmList listKey="projects" />}
          {section === 'winners' && <AdmList listKey="winners" />}
          {section === 'team' && <AdmList listKey="team" />}
          {section === 'users' && <AdmUsers />}
          {section === 'notify' && <AdmNotify />}
          {section === 'settings' && <AdmSettings />}
        </div>
      </main>

      {/* ===== мобильный нижний док ===== */}
      {!isEditor && (
        <nav className="fed-adm-dock">
          {DOCK.map((item) => {
            const on = item.key === 'more' ? !PRIMARY_KEYS.includes(activeGroup) : activeGroup === item.key;
            const badge = item.badge ? badges[item.badge] : 0;
            return (
              <button
                key={item.key}
                className={`fed-adm-tab ${on ? 'on' : ''}`}
                onClick={() => goSection(item.key)}
              >
                <span className="relative inline-flex">
                  <Ic name={item.icon} size={22} sw={on ? 2.1 : 1.85} />
                  {badge > 0 && <span className="dot">{badge > 9 ? '9+' : badge}</span>}
                </span>
                <span className="l">{item.label}</span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}
